import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';

import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { MwAuthService } from '../../auth/services/auth.service';
import { companyActions, companyReducers, userReducers } from '../@store';
import { NamingTypeModel } from '../models/naming-type.model';

@Injectable()
export class MwCompanyService {
  constructor(
    private store: Store<any>,
    private authService: MwAuthService,
    private actions$: Actions
  ) {}

  get featureFlagsState$(): Observable<{
    [key: string]: boolean;
  }> {
    return this.store.pipe(
      select(companyReducers.areFeatureFlagsReady),
      filter((areReady) => areReady),
      switchMap(() => this.store.pipe(select(companyReducers.getFeatureFlags)))
    );
  }

  get activeCompanyId$(): Observable<number> {
    return this.authService.activeCompany$.pipe(
      map((company) => company?.companyId || 0)
    );
  }

  get instantActiveCompanyId(): number {
    let companyId = 0;

    this.activeCompanyId$
      .pipe(take(1))
      .subscribe((localCompanyId) => (companyId = localCompanyId));

    return companyId;
  }

  get hasManyCompanies$(): Observable<boolean> {
    return this.store.pipe(select(userReducers.hasManyCompanies));
  }

  get namingTypes$(): Observable<NamingTypeModel> {
    return this.store.pipe(select(companyReducers.namingType));
  }

  get dateFormat$(): Observable<string> {
    return this.store.pipe(select(companyReducers.dateFormat));
  }

  clearPreference(): void {
    this.store.dispatch(companyActions.clearPreference());
  }

  requestFeatureFlagState(key: string, companyId: number): void {
    this.store.dispatch(
      companyActions.requestFeatureFlagState({
        key,
        companyId,
      })
    );
  }

  getFeatureFlagState(key: string): Observable<boolean> {
    return this.store.select(companyReducers.getFeatureFlagState(key)).pipe(
      take(1),
      switchMap((state) => {
        const isUndefined = state === undefined;
        const companyId = this.instantActiveCompanyId;
        if (isUndefined) {
          this.requestFeatureFlagState(key, companyId);
          const result = this.actions$.pipe(
            ofType(companyActions.featureFlagReceived),
            filter(
              (action) => action.key === key && action.companyId === companyId
            ),
            take(1),
            map((action) => action.isEnabled)
          );
          return result;
        } else {
          return of(state);
        }
      })
    );
  }
}
